import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { BlogPublicCategoryNodeDto } from '@/api';
import { BlogPublicService } from '@/api';
import { getErrorMessage } from '@/utils/error.utils';

export interface BlogNavState {
  roots: BlogPublicCategoryNodeDto[];
  loading: boolean;
  error: string | null;
  loaded: boolean;
}

const initialState: BlogNavState = {
  roots: [],
  loading: false,
  error: null,
  loaded: false,
};

@Injectable({ providedIn: 'root' })
export class BlogNavStore {
  private readonly api = inject(BlogPublicService);

  private state = signal<BlogNavState>(initialState);
  private loadInFlight: Promise<void> | null = null;

  roots = computed(() => this.state().roots);
  loading = computed(() => this.state().loading);
  error = computed(() => this.state().error);
  loaded = computed(() => this.state().loaded);

  hasPublishedBlogContent = computed(() => this.roots().length > 0);

  async loadNavigation(): Promise<void> {
    if (this.loaded()) return;
    if (this.loadInFlight) return this.loadInFlight;

    this.loadInFlight = this.fetchNavigation().finally(() => {
      this.loadInFlight = null;
    });
    return this.loadInFlight;
  }

  /** Повторная загрузка после ошибки или для явного обновления. */
  async reloadNavigation(): Promise<void> {
    this.state.set(initialState);
    this.loadInFlight = null;
    return this.loadNavigation();
  }

  private async fetchNavigation(): Promise<void> {
    this.state.update((s) => ({ ...s, loading: true, error: null }));
    try {
      const roots = await firstValueFrom(this.api.getBlogPublicNavigation());
      this.state.update((s) => ({
        ...s,
        roots: roots ?? [],
        loading: false,
        loaded: true,
      }));
    } catch (e: unknown) {
      const msg = getErrorMessage(e, 'Не удалось загрузить навигацию блога');
      this.state.update((s) => ({
        ...s,
        roots: [],
        error: msg,
        loading: false,
        loaded: true,
      }));
    }
  }
}
